/**
 * Evashu PDP Reviews V3 — proximity-gated Judge.me presentation bridge.
 * The branded metafield summary stays server-rendered. Judge.me remains owner
 * of review content, actions, filters and pagination. This bridge waits until
 * the review area is near the viewport, then hides only duplicated native
 * heading/rating summary with one rAF-throttled observer.
 */
(() => {
  'use strict';

  const textOf = (el) => (el.textContent || '').replace(/\s+/g, ' ').trim();

  const markDuplicates = (root, summary) => {
    if (!root?.isConnected || !summary?.isConnected) return;

    const count = summary.dataset.eihCount || '';
    const avg = summary.dataset.eihAverageDisplay || '';
    const reviewSelector = '.jm-review-item,.jdgm-rev';

    root.querySelectorAll('h1,h2,h3,h4,p,span,div').forEach((el) => {
      if (el.closest(reviewSelector)) return;
      if (el.matches('button,[role="button"],input,select,textarea,label')) return;
      if (el.querySelector('button,input,select,textarea')) return;

      const text = textOf(el);
      if (!text || text.length > 60) return;
      if (/^Customer Reviews?$/i.test(text)) el.classList.add('eih-jm-native-heading');
    });

    root.querySelectorAll('div,section').forEach((el) => {
      if (el.closest(reviewSelector)) return;
      if (el.querySelector('button,input,select,textarea')) return;

      const text = textOf(el);
      if (!text || text.length > 100) return;

      const escapedAvg = avg.replace('.', '\\.');
      const hasAverage = avg && new RegExp(`(^|\\s)${escapedAvg}($|\\s)`).test(text);
      const hasCount = count && new RegExp(`\\b${count}\\s+reviews?\\b`, 'i').test(text);
      if (hasAverage && hasCount) el.classList.add('eih-jm-native-rating-summary');
    });

    root.dataset.eihReviewStyled = 'true';
  };

  const activate = (root, summary) => {
    if (root._eihReviewBridgeActive) return;
    root._eihReviewBridgeActive = true;

    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        markDuplicates(root, summary);
      });
    };

    schedule();

    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true });
    root._eihReviewObserver = observer;
  };

  const init = () => {
    const summary = document.querySelector('.eih-pdp-review-summary');
    const root = document.querySelector('#judgeme_product_reviews');
    if (!summary || !root || root._eihReviewBridgeActive || root._eihReviewBridgePending) return;

    if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      activate(root, summary);
      return;
    }

    root._eihReviewBridgePending = true;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      io.disconnect();
      root._eihReviewBridgePending = false;
      activate(root, summary);
    }, { rootMargin: '900px 0px', threshold: 0 });

    io.observe(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
